// The settle draft (OH-193 spec §4.4, revision 4): what the confirmation
// windows will send, kept apart from the server's suggestion and from the
// base the draft was assembled against. A pure state machine — every
// transition the plan's table names (S1–S5, R1–R3′, C1–C4, E1–E4, X1, A1–A2,
// K1–K2) is a branch of reduceDraft below and nothing else; the hooks and
// dialogs only dispatch events and read the admission predicates.
//
// The fee mode («the bank kept the difference», `sendsBank`, OH-205 §6, R3′)
// is a flag on the request shape over either way of counting, never a way of
// its own: the payment amount stays what arrived and stays editable, the
// settlement is what arrived plus the shortfall line the server suggests, and
// turning the mode on or off moves no amount the operator typed.
//
// Money stays a major-unit string with its own currency and two flags:
// `manual` (the operator typed it — a suggestion never overwrites it) and
// `stale` (typed under a context that has since changed — sticky until that
// very value is typed again, §4.4 p.3). Arithmetic goes through lib/money
// and lib/financeMath only: the equation on screen is the server's.
import type {
  FinanceLineInput,
  FinanceSettleExpect,
  FinanceSettlePreviewResponse,
} from '@/api/generated/types.gen';
import { computeBatchEquation, computeSettlePreview } from './financeMath';
import { fromMinor, toMinor } from './money';
import { atPlan, convertMinor, type PlanComparison } from './planDeviation';

export type TargetKey = `expected:${string}` | `schedule_entry:${string}`;
export type LineKind = 'income' | 'expense' | 'withheld';
// stale: entered under a context that has since changed (sticky until this value is edited) [PR-01]
export interface Money {
  major: string;
  currency: string;
  manual: boolean;
  stale: boolean;
}
export interface DraftLine {
  key: string; // suggested: `${source}:${targetKey ?? '-'}`; operator: a fresh uuid
  source: 'account_fee' | 'provider_fee' | 'shortfall' | 'operator';
  kind: LineKind; // suggested lines are always 'withheld'; operator lines keep the form's kind [PR-02]
  categoryId: string;
  amount: Money;
  targetKey: TargetKey | null;
  labelManual: boolean; // the operator chose the article (or, operator lines, the kind) — syncFrom keeps it [PR2-01]
  orphan: boolean; // bound to a target that is no longer in the context
}
// `${source}:${targetKey ?? '-'}` — the identity of a suggested line across responses
export type SuggestionKey = string;
export interface DraftContext {
  accountId: string;
  currency: string;
  occurredOn: string;
  targets: TargetKey[];
}
export interface DraftBase {
  context: DraftContext;
  expect: FinanceSettleExpect;
}
// the answer AND what it answers: the context [PR-01] and whether the request carried the bank amount [PR3-01]
export interface Suggestion {
  response: FinanceSettlePreviewResponse;
  context: DraftContext;
  sentBank: boolean;
}
export interface ConflictRow {
  key: TargetKey | `rate:${string}`;
  was: string;
  now: string;
}
// OH-201: which amount the single form counts from. 'settle' — the payment
// amount itself (the single target's amount): the bank total follows from it
// and the lines, so the bank is derived, never typed money. 'bank' — the
// bank total, the payment amount follows (OH-193; the batch and the
// projection always count this way). The fee mode (OH-205, R3′) is no anchor:
// it works over either. From the payment amount, that amount is what arrived
// and the settlement is it plus the shortfall line; from the bank, what
// arrived is derived from the bank and the operator's lines. The single form
// never holds both as typed money: switching which one it counts from
// releases the other.
export type Anchor = 'bank' | 'settle';
export interface DraftState {
  suggestion: Suggestion | null;
  base: DraftBase | null;
  context: DraftContext;
  bank: Money;
  targetAmounts: Partial<Record<TargetKey, Money>>;
  lines: DraftLine[]; // only lines that exist; a removed suggested line is NOT here
  removedKeys: SuggestionKey[]; // the operator's decision "not this suggested line" — no money, survives refetches [PR2-02]
  sendsBank: boolean; // the request shape the form is in NOW: batch always true; single only in the fee mode — a flag over either anchor (R3′) [PR3-01]
  anchor: Anchor;
  status: 'empty' | 'clean' | 'conflict' | 'stale';
  conflict: ConflictRow[];
}
export type DraftEvent =
  | { type: 'suggestion'; response: FinanceSettlePreviewResponse; context: DraftContext; sentBank: boolean }
  | { type: 'context'; context: DraftContext }
  | { type: 'requestShape'; sendsBank: boolean } // single form: the remainder mode switched to / from «fee» [PR3-01]
  | { type: 'editBank'; major: string }
  | { type: 'editSettle'; major: string } // single form: the payment amount of its one target (OH-201); in the fee mode, what arrived (R3′)
  | { type: 'editTargetAmount'; target: TargetKey; major: string }
  | { type: 'addLine'; kind: LineKind; categoryId: string; major: string; targetKey: TargetKey | null }
  | { type: 'editLine'; key: string; patch: { categoryId?: string; kind?: LineKind; major?: string } } // kind: operator lines only
  | { type: 'removeLine'; key: string }
  | { type: 'applySuggestion' }
  | { type: 'keepMyAmounts' };

type ExpectTarget = FinanceSettleExpect['targets'][number];
type ExpectRate = FinanceSettleExpect['rates'][number];

const emptyMoney = (currency: string): Money => ({ major: '', currency, manual: false, stale: false });
const typed = (major: string, currency: string): Money => ({ major, currency, manual: true, stale: false });
const suggestedMoney = (minor: number, currency: string): Money => ({
  major: fromMinor(minor, currency),
  currency,
  manual: false,
  stale: false,
});

export function targetKey(t: { expected_id?: string | null; schedule_entry_id?: string | null }): TargetKey {
  if (t.expected_id) return `expected:${t.expected_id}`;
  if (t.schedule_entry_id) return `schedule_entry:${t.schedule_entry_id}`;
  throw new Error('settle target has neither expected_id nor schedule_entry_id');
}

/** The target part of a suggestion key (`account_fee:schedule_entry:e1` → `schedule_entry:e1`; `shortfall:-` → null). */
function keyTarget(key: SuggestionKey): string | null {
  const rest = key.slice(key.indexOf(':') + 1);
  return rest === '-' ? null : rest;
}

export function initialDraft(context: DraftContext, sendsBank: boolean, anchor: Anchor = 'bank'): DraftState {
  return {
    suggestion: null,
    base: null,
    context,
    bank: emptyMoney(context.currency),
    targetAmounts: {},
    lines: [],
    removedKeys: [],
    sendsBank,
    anchor,
    status: 'empty',
    conflict: [],
  };
}

const sameTargetSet = (a: readonly TargetKey[], b: readonly TargetKey[]) => {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((k) => set.has(k));
};

/** Account, currency, date and the target SET (order ignored, [PR-01]). */
export function sameContext(a: DraftContext, b: DraftContext): boolean {
  return a.accountId === b.accountId && a.currency === b.currency && a.occurredOn === b.occurredOn && sameTargetSet(a.targets, b.targets);
}

/** The suggestion answers the draft's own context AND its current request shape [PR3-01]. */
export function suggestionIsCurrent(s: DraftState): boolean {
  return s.suggestion !== null && sameContext(s.suggestion.context, s.context) && s.suggestion.sentBank === s.sendsBank;
}

/**
 * A stale value's own message, in the stale panel's words: another currency
 * is named; the same currency (another account, another set of payments, or
 * back to the currency it was typed in) is not one to blame.
 */
export function staleFieldError(m: Money, currency: string): string | undefined {
  if (!m.stale) return undefined;
  return m.currency !== currency ? `Entered for ${m.currency} — re-enter it` : 'Entered before the account or the payments changed — re-enter it';
}

export function liveLines(s: DraftState): DraftLine[] {
  return s.lines.filter((l) => !l.orphan);
}

/**
 * The money a submit would send: the bank, the context targets' amounts, the
 * live lines' amounts [PR2-02]. Counting from the payment amount (OH-201) the
 * bank is derived from the others, so it is not among them.
 */
export function submittedMoney(s: DraftState): Money[] {
  const targets = s.context.targets.map((k) => s.targetAmounts[k]).filter((m): m is Money => m !== undefined);
  return [...(s.anchor === 'settle' ? [] : [s.bank]), ...targets, ...liveLines(s).map((l) => l.amount)];
}

const hasManualMoney = (s: DraftState) =>
  s.bank.manual || Object.values(s.targetAmounts).some((m) => m?.manual) || s.lines.some((l) => l.amount.manual);

/** A value in the context's currency, as minor units; null when it does not parse or is in another currency. */
function minorOf(m: Money | undefined, currency: string): number | null {
  if (!m || m.currency !== currency) return null;
  try {
    return toMinor(m.major, currency);
  } catch {
    return null; // a currency lib/money does not know — not a number this form can send
  }
}

// ---- base -------------------------------------------------------------------

function baseFrom(resp: FinanceSettlePreviewResponse, ctx: DraftContext): DraftBase {
  return {
    context: ctx,
    expect: {
      // the reference stays flat, one field per target — never the other one as undefined [PR2-03]
      targets: resp.targets.map(
        (t): ExpectTarget => ({
          ...(t.expected_id ? { expected_id: t.expected_id } : {}),
          ...(t.schedule_entry_id ? { schedule_entry_id: t.schedule_entry_id } : {}),
          remaining_minor: t.remaining_minor,
          currency: t.target_currency,
        }),
      ),
      // a rate used only for a fee's fixed part is not a movement rate — not in the base (§4.5, [PR-T1])
      rates: resp.rates
        .filter((r) => r.purposes.includes('movement'))
        .map(({ currency, rate_micro, rate_date }): ExpectRate => ({ currency, rate_micro, rate_date })),
    },
  };
}

const formatRemaining = (t: ExpectTarget | undefined) => {
  if (!t) return '—';
  try {
    return `${fromMinor(t.remaining_minor, t.currency)} ${t.currency}`;
  } catch {
    return `${t.remaining_minor} ${t.currency} (minor units)`;
  }
};
const formatRate = (r: ExpectRate | undefined) =>
  r ? `${Math.floor(r.rate_micro / 1_000_000)}.${String(r.rate_micro % 1_000_000).padStart(6, '0')} @ ${r.rate_date}` : '—';

/**
 * The «was → now» rows between two bases: a target whose remaining or
 * currency differs, a rate whose value or date differs, and anything present
 * in only one of them. `commonOnly` (X1) keeps only the targets and rate
 * currencies present in both. Empty ⇔ the bases agree.
 */
function baseDiff(old: DraftBase, next: DraftBase, commonOnly = false): ConflictRow[] {
  const rows: ConflictRow[] = [];
  const oldT = new Map(old.expect.targets.map((t) => [targetKey(t), t]));
  const nextT = new Map(next.expect.targets.map((t) => [targetKey(t), t]));
  for (const k of new Set([...oldT.keys(), ...nextT.keys()])) {
    const a = oldT.get(k);
    const b = nextT.get(k);
    if (commonOnly && (!a || !b)) continue;
    if (a && b && a.remaining_minor === b.remaining_minor && a.currency === b.currency) continue;
    rows.push({ key: k, was: formatRemaining(a), now: formatRemaining(b) });
  }
  const oldR = new Map(old.expect.rates.map((r) => [r.currency, r]));
  const nextR = new Map(next.expect.rates.map((r) => [r.currency, r]));
  for (const c of new Set([...oldR.keys(), ...nextR.keys()])) {
    const a = oldR.get(c);
    const b = nextR.get(c);
    if (commonOnly && (!a || !b)) continue;
    if (a && b && a.rate_micro === b.rate_micro && a.rate_date === b.rate_date) continue;
    rows.push({ key: `rate:${c}`, was: formatRate(a), now: formatRate(b) });
  }
  return rows;
}

// ---- syncFrom -----------------------------------------------------------------

/**
 * Copy the response's values into everything the operator has not touched
 * ([PR-06]): the bank and target amounts unless manual; each suggested line by
 * its key (source + the target of ITS response's target_index), the amount
 * unless manual and the article unless labelManual; a key in removedKeys is
 * not re-created; a suggested line the response no longer has is dropped
 * unless it carries any manual edit — except the shortfall line, which only a
 * manual AMOUNT keeps (OH-212). The batch's automatic Fee line is the gap
 * between the payments and the bank typed, so with the gap gone a line kept
 * for its re-labelled article alone is a fee that does not exist, and the batch
 * window (which locks that amount and offers no remove) could never get rid of
 * it. While the response still carries the shortfall the operator's article is
 * kept as for any line (the `seen` branch). New values are in the context currency.
 */
function syncFrom(s: DraftState, resp: FinanceSettlePreviewResponse): DraftState {
  const cur = s.context.currency;
  const bank = s.bank.manual ? s.bank : suggestedMoney(resp.suggested_bank_amount_minor, cur);
  const targetAmounts = { ...s.targetAmounts };
  for (const t of resp.targets) {
    const k = targetKey(t);
    if (!targetAmounts[k]?.manual) targetAmounts[k] = suggestedMoney(t.planned_minor, cur);
  }
  const suggested = new Map<SuggestionKey, FinanceSettlePreviewResponse['suggested_lines'][number] & { targetKey: TargetKey | null }>();
  for (const l of resp.suggested_lines) {
    const t = l.target_index != null ? resp.targets[l.target_index] : undefined;
    const tk = t ? targetKey(t) : null;
    suggested.set(`${l.source}:${tk ?? '-'}`, { ...l, targetKey: tk });
  }
  const lines: DraftLine[] = [];
  const seen = new Set<SuggestionKey>();
  for (const l of s.lines) {
    if (l.source === 'operator') {
      lines.push(l);
      continue;
    }
    const sl = suggested.get(l.key);
    if (!sl || s.removedKeys.includes(l.key)) {
      // gone from the answer: kept only with a manual edit — a shortfall line only with a manual amount (see above)
      if (l.amount.manual || (l.labelManual && l.source !== 'shortfall')) lines.push(l);
      continue;
    }
    seen.add(l.key);
    lines.push({
      ...l,
      amount: l.amount.manual ? l.amount : suggestedMoney(sl.amount_minor, cur),
      categoryId: l.labelManual ? l.categoryId : sl.category_id,
    });
  }
  for (const [key, sl] of suggested) {
    if (seen.has(key) || s.removedKeys.includes(key) || lines.some((l) => l.key === key)) continue;
    lines.push({
      key,
      source: sl.source,
      kind: 'withheld',
      categoryId: sl.category_id,
      amount: suggestedMoney(sl.amount_minor, cur),
      targetKey: sl.targetKey,
      labelManual: false,
      orphan: false,
    });
  }
  return { ...s, bank, targetAmounts, lines };
}

// ---- transitions ------------------------------------------------------------

/** X1: leave `stale` once every stale value is re-entered (or removed), no orphan is left and the suggestion is current. */
function tryLeaveStale(s: DraftState): DraftState {
  if (s.status !== 'stale') return s;
  if (submittedMoney(s).some((m) => m.stale)) return s;
  if (s.lines.some((l) => l.orphan)) return s;
  if (!suggestionIsCurrent(s) || !s.suggestion) return s;
  const resp = s.suggestion.response;
  const next = baseFrom(resp, s.context);
  if (s.base === null) return { ...syncFrom(s, resp), base: next, status: 'clean', conflict: [] };
  const rows = baseDiff(s.base, next, true);
  if (rows.length === 0) return { ...syncFrom(s, resp), base: next, status: 'clean', conflict: [] };
  return { ...s, status: 'conflict', conflict: rows };
}

function onSuggestion(s: DraftState, e: Extract<DraftEvent, { type: 'suggestion' }>): DraftState {
  // S1: an answer to another context or another request shape — dropped, same object
  if (!sameContext(e.context, s.context) || e.sentBank !== s.sendsBank) return s;
  const suggestion: Suggestion = { response: e.response, context: e.context, sentBank: e.sentBank };
  const next = baseFrom(e.response, s.context);
  switch (s.status) {
    case 'empty': // S2: the first filling
      return { ...syncFrom({ ...s, suggestion }, e.response), base: next, status: 'clean', conflict: [] };
    case 'clean':
    case 'conflict': {
      // S3 / S4: compared with the base the draft holds — never replaced silently
      const rows = s.base ? baseDiff(s.base, next) : [];
      if (rows.length === 0) return { ...syncFrom({ ...s, suggestion }, e.response), status: 'clean', conflict: [] };
      return { ...s, suggestion, status: 'conflict', conflict: rows };
    }
    case 'stale': // S5: only remembered; staleness outranks a conflict
      return tryLeaveStale({ ...s, suggestion });
  }
}

function onContext(s: DraftState, ctx: DraftContext): DraftState {
  // C1: the same context, maybe the targets in another order
  if (sameContext(ctx, s.context)) return { ...s, context: ctx };
  // C2: only the date
  if (ctx.accountId === s.context.accountId && ctx.currency === s.context.currency && sameTargetSet(ctx.targets, s.context.targets)) {
    return { ...s, context: ctx };
  }
  const inNew = new Set<string>(ctx.targets);
  if (!hasManualMoney(s)) {
    // C3: reset; a removal marker for a target still present (or the shortfall's) survives;
    // a line with only a manual article stays, its amount cleared — and, bound to a
    // departed target, becomes an orphan (it needs an explicit removal, like C4)
    return {
      ...s,
      context: ctx,
      bank: emptyMoney(ctx.currency),
      targetAmounts: {},
      lines: s.lines
        .filter((l) => l.labelManual)
        .map((l) => ({
          ...l,
          amount: emptyMoney(ctx.currency),
          orphan: l.orphan || (l.targetKey !== null && !inNew.has(l.targetKey)),
        })),
      removedKeys: s.removedKeys.filter((k) => {
        const t = keyTarget(k);
        return t === null || inNew.has(t);
      }),
      base: null,
      status: 'empty',
      conflict: [],
    };
  }
  // C4: stale. Every manual value is marked (sticky); non-manual values were in the old
  // currency and are cleared; lines bound to a departed target become orphans; the
  // departed targets' amounts go; conflict rows go — staleness outranks them.
  const mark = (m: Money): Money => (m.manual ? { ...m, stale: true } : emptyMoney(ctx.currency));
  const targetAmounts: Partial<Record<TargetKey, Money>> = {};
  for (const [k, m] of Object.entries(s.targetAmounts) as [TargetKey, Money | undefined][]) {
    if (m?.manual && inNew.has(k)) targetAmounts[k] = { ...m, stale: true };
  }
  const lines = s.lines
    .filter((l) => l.source === 'operator' || l.amount.manual || l.labelManual)
    .map((l) => ({
      ...l,
      amount: mark(l.amount),
      orphan: l.orphan || (l.targetKey !== null && !inNew.has(l.targetKey)),
    }));
  return { ...s, context: ctx, bank: mark(s.bank), targetAmounts, lines, status: 'stale', conflict: [] };
}

/**
 * OH-201: switching which amount the single form counts from releases the
 * other one — the operator's value goes, its stale mark with it, and the
 * suggestion fills it: at once on a clean draft over a current suggestion,
 * otherwise with the next syncFrom (S2/S3, X1, K1, A2). Left typed, it would
 * still be checked, sent and compared as if the operator stood by it.
 */
function release(s: DraftState, side: Anchor): DraftState {
  const target = s.context.targets[0];
  const next: DraftState =
    side === 'bank'
      ? { ...s, bank: emptyMoney(s.context.currency) }
      : { ...s, targetAmounts: Object.fromEntries(Object.entries(s.targetAmounts).filter(([k]) => k !== target)) };
  return next.status === 'clean' && suggestionIsCurrent(next) && next.suggestion ? syncFrom(next, next.suggestion.response) : next;
}

function onRequestShape(s: DraftState, sendsBank: boolean): DraftState {
  if (sendsBank === s.sendsBank) return s; // R1
  // R3′ (OH-205 §6): the fee mode is a flag over either way of counting — the anchor and every amount
  // stay as they are; the request now asks about what arrived (previewBankMinor) and the shortfall
  // line comes with the answer (S3)
  if (sendsBank) return { ...s, sendsBank };
  // R2: a shortfall exists only with the bank in the request — an untouched shortfall line goes now
  return {
    ...s,
    sendsBank,
    lines: s.lines.filter((l) => l.source !== 'shortfall' || l.amount.manual || l.labelManual),
  };
}

export function reduceDraft(s: DraftState, e: DraftEvent): DraftState {
  switch (e.type) {
    case 'suggestion':
      return onSuggestion(s, e);
    case 'context':
      return onContext(s, e.context);
    case 'requestShape':
      return onRequestShape(s, e.sendsBank);
    case 'editBank': {
      // E1; the form now counts from the bank amount, the payment amount released on the switch (OH-201)
      const next: DraftState = { ...s, bank: typed(e.major, s.context.currency), anchor: 'bank' };
      return tryLeaveStale(s.anchor === 'settle' ? release(next, 'settle') : next);
    }
    case 'editSettle': {
      // E1 for the single form's one target; the form now counts from the payment amount, the bank
      // amount released on the switch (OH-201). The fee mode takes it too (R3′): there it is what arrived.
      const target = s.context.targets[0];
      if (!target) return s;
      const next: DraftState = {
        ...s,
        targetAmounts: { ...s.targetAmounts, [target]: typed(e.major, s.context.currency) },
        anchor: 'settle',
      };
      return tryLeaveStale(s.anchor === 'bank' ? release(next, 'bank') : next);
    }
    case 'editTargetAmount': // E1
      return tryLeaveStale({ ...s, targetAmounts: { ...s.targetAmounts, [e.target]: typed(e.major, s.context.currency) } });
    case 'addLine': // E2
      return tryLeaveStale({
        ...s,
        lines: [
          ...s.lines,
          {
            key: crypto.randomUUID(),
            source: 'operator',
            kind: e.kind,
            categoryId: e.categoryId,
            amount: typed(e.major, s.context.currency),
            targetKey: e.targetKey,
            labelManual: true,
            orphan: false,
          },
        ],
      });
    case 'editLine': {
      const { major, categoryId, kind } = e.patch;
      const lines = s.lines.map((l) => {
        if (l.key !== e.key) return l;
        let next = l;
        // E1: re-entered money — the context currency, manual, no longer stale
        if (major !== undefined) next = { ...next, amount: typed(major, s.context.currency) };
        // E4: the article (and an operator line's kind) — the amount, its currency and staleness untouched
        if (categoryId !== undefined) next = { ...next, categoryId, labelManual: true };
        if (kind !== undefined && l.source === 'operator') next = { ...next, kind, labelManual: true };
        return next;
      });
      const next = { ...s, lines };
      return major !== undefined ? tryLeaveStale(next) : next;
    }
    case 'removeLine': {
      // E3: the line leaves with its money and its stale flag; a suggested one leaves a marker
      const l = s.lines.find((x) => x.key === e.key);
      if (!l) return s;
      return tryLeaveStale({
        ...s,
        lines: s.lines.filter((x) => x.key !== e.key),
        removedKeys: l.source !== 'operator' && !s.removedKeys.includes(l.key) ? [...s.removedKeys, l.key] : s.removedKeys,
      });
    }
    case 'applySuggestion': {
      // A1: nothing to apply until the suggestion answers the current context [PR-01]
      if (!suggestionIsCurrent(s) || !s.suggestion) return s;
      // A2: everything from the suggestion — no manual flags, no stale, no orphans, no markers, no
      // operator lines; the form keeps counting the way it did (OH-201). In the fee mode counting from the
      // payment amount (R3′) the payment is the plan again, and the shortfall line of the held answer belongs
      // to the arrival this discards: the lines come from the answer without it, so nothing reads as short
      // until the answer to the request without a bank lands. The base keeps the whole answer.
      const fresh: DraftState = { ...initialDraft(s.context, s.sendsBank, s.anchor), suggestion: s.suggestion };
      const fill = s.sendsBank && s.anchor === 'settle' ? withoutShortfall(s.suggestion.response) : s.suggestion.response;
      return { ...syncFrom(fresh, fill), base: baseFrom(s.suggestion.response, s.context), status: 'clean', conflict: [] };
    }
    case 'keepMyAmounts': {
      // K2
      if (s.status !== 'conflict' || !suggestionIsCurrent(s) || !s.suggestion) return s;
      // K1: the operator confirms their amounts against the new plan
      return { ...syncFrom(s, s.suggestion.response), base: baseFrom(s.suggestion.response, s.context), status: 'clean', conflict: [] };
    }
  }
}

// ---- admission --------------------------------------------------------------

type BlockerCode = FinanceSettlePreviewResponse['blockers'][number]['code'];
/**
 * Blockers that say something about the suggestion, not about the draft: a
 * `difference` is about the bank amount in the REQUEST (the form shows the
 * draft's own); `fee_category_unusable` only means no fee line was suggested —
 * the operator enters the fee as a line by hand (owner, 2026-10-20: not fail-closed).
 */
export const ADVISORY_BLOCKERS: ReadonlySet<BlockerCode> = new Set<BlockerCode>(['difference', 'fee_category_unusable']);

/** status/base/suggestion/blockers gate, no money rule; an orphan line (outside `stale` too) blocks until it is removed. */
export function draftReady(s: DraftState): boolean {
  if (s.status !== 'clean' || s.base === null || !suggestionIsCurrent(s) || !s.suggestion) return false;
  if (s.lines.some((l) => l.orphan)) return false;
  if (s.suggestion.response.blockers.some((b) => !ADVISORY_BLOCKERS.has(b.code))) return false;
  return submittedMoney(s).every((m) => minorOf(m, s.context.currency) !== null);
}

/** bank − (Σtargets + Σincome + Σexpense − Σwithheld) via computeBatchEquation; null when a value does not parse or is in another currency. */
export function draftDifferenceMinor(s: DraftState): number | null {
  const cur = s.context.currency;
  const bankMinor = minorOf(s.bank, cur);
  if (bankMinor === null) return null;
  const targets: number[] = [];
  for (const k of s.context.targets) {
    const v = minorOf(s.targetAmounts[k], cur);
    if (v === null) return null;
    targets.push(v);
  }
  const lines = linesMinor(s);
  if (lines === null) return null;
  return 0 - computeBatchEquation({ targets, lines, bankMinor }).diff; // 0 −, not unary −: never -0
}

function linesMinor(s: DraftState): { kind: LineKind; amountMinor: number }[] | null {
  const out: { kind: LineKind; amountMinor: number }[] = [];
  for (const l of liveLines(s)) {
    const v = minorOf(l.amount, s.context.currency);
    if (v === null) return null;
    out.push({ kind: l.kind, amountMinor: v });
  }
  return out;
}

// ---- the batch: the bank amount follows the payments (OH-212) --------------------------------------------------

/**
 * The context targets' amounts and the live lines, in minor units, as computeBatchEquation takes them; null when any of
 * them does not parse or is in another currency. `skipShortfall` leaves the suggested shortfall lines out (batchTypedBank).
 */
function batchTermsMinor(
  s: DraftState,
  skipShortfall: boolean,
): { targets: number[]; lines: { kind: LineKind; amountMinor: number }[] } | null {
  const cur = s.context.currency;
  const targets: number[] = [];
  for (const k of s.context.targets) {
    const v = minorOf(s.targetAmounts[k], cur);
    if (v === null) return null;
    targets.push(v);
  }
  const lines: { kind: LineKind; amountMinor: number }[] = [];
  for (const l of liveLines(s)) {
    if (skipShortfall && l.source === 'shortfall') continue;
    const v = minorOf(l.amount, cur);
    if (v === null) return null;
    lines.push({ kind: l.kind, amountMinor: v });
  }
  return { targets, lines };
}

/**
 * The batch's bank amount, derived (OH-212): what the account ends up with — the context targets' amounts, a `withheld`
 * line taking from them, `income` and `expense` lines adding (for an outgoing batch: what leaves the account). The
 * header (`amount_minor`) is this number, so it balances by construction and there is no difference left to check.
 * Null when an amount or a live line does not parse or is in another currency.
 */
export function batchBankMinor(s: DraftState): number | null {
  const terms = batchTermsMinor(s, false);
  return terms === null ? null : computeBatchEquation({ ...terms, bankMinor: 0 }).sum;
}

/**
 * The plan of all the rows together, in the account currency (OH-212); null until the draft holds an answer to its own
 * context and request shape.
 */
export function batchPlannedTotalMinor(s: DraftState): number | null {
  return suggestionIsCurrent(s) && s.suggestion ? s.suggestion.response.planned_total_minor : null;
}

/**
 * A bank amount the operator typed into the batch, as the request that gets its fee (OH-212). The bank amount is what the
 * account ends up with, so the gap between the payments with their lines and the typed amount is the fee (`feeMinor`).
 * Every live line counts but the shortfall: that one answers an earlier ask and the next answer replaces it. The server
 * suggests a shortfall of «plan − bank», so asking with `arrivalMinor` = plan − fee gets exactly this fee back. It suggests
 * that shortfall only for an all-incoming batch with no fee rule and no provider fee, and only when the article it would
 * book the fee on is usable (otherwise the answer names `fee_category_unusable` and carries none); a shortfall line the
 * operator edited or removed survives later answers, which is why the window locks it. A typed amount above the payments
 * with their lines has no fee to explain it: the ask stays the plan and `overMinor` names the excess. Null when the plan or
 * an amount that counts is unknown, or when nothing would arrive.
 */
export function batchTypedBank(
  s: DraftState,
  typedMinor: number,
): { arrivalMinor: number; feeMinor: number; overMinor: number } | null {
  const planned = batchPlannedTotalMinor(s);
  const terms = batchTermsMinor(s, true);
  if (planned === null || terms === null) return null;
  const gap = computeBatchEquation({ ...terms, bankMinor: typedMinor }).diff; // payments less the lines − typed
  const feeMinor = Math.max(0, gap);
  const arrivalMinor = planned - feeMinor;
  return arrivalMinor > 0 ? { arrivalMinor, feeMinor, overMinor: Math.max(0, -gap) } : null;
}

/**
 * A batch bank amount whose answer has not landed (OH-212): Confirm waits while the answer held is not the one to the ask
 * the draft stands on.
 * - Typed: the server echoes the request's bank amount in suggested_bank_amount_minor, so a held answer that does not echo
 *   the typed bank belongs to another ask; its shortfall line is not the fee this bank implies.
 * - Not typed — nobody typed it, or Apply / Reset to suggestion discarded it: nothing is asked about it, and the server
 *   suggests a shortfall only for a request that carries a bank. A held answer that carries one is therefore the answer to
 *   the ask that was discarded, whose fee the refill has just brought back into the draft; the answer to the request that
 *   goes out now, without a bank, carries none.
 * - Stale with the context: nothing is asked for it, so nothing is waited for.
 */
export function batchBankPending(s: DraftState): boolean {
  const held = suggestionIsCurrent(s) ? s.suggestion : null;
  if (!s.bank.manual) return held !== null && held.response.suggested_lines.some((l) => l.source === 'shortfall');
  if (s.bank.stale) return false;
  return held === null || held.response.suggested_bank_amount_minor !== minorOf(s.bank, s.context.currency);
}

/**
 * The batch's admission (OH-212): a ready draft, no typed bank amount still waiting for its answer, and a derived header
 * above zero. The header is derived (batchBankMinor), so it balances by construction — the zero-difference rule is gone
 * (draftDifferenceMinor stays for the projection window, whose bank amount is a fixed fact).
 */
export function canSubmitBatch(s: DraftState): boolean {
  return draftReady(s) && !batchBankPending(s) && (batchBankMinor(s) ?? 0) > 0;
}

/**
 * The single settle's own settle line. Counting from the payment amount
 * (OH-201) it is the target's amount as typed or suggested — in the fee mode
 * (R3′) that amount plus the shortfall line(s), the settlement the server
 * derives from what the request carries; counting from the bank, the
 * window's formula with dependents (computeSettlePreview) [PR-03].
 */
export function singleSettleMinor(s: DraftState, dependentsMinor: number[]): number | null {
  if (s.anchor === 'settle') {
    const target = s.context.targets[0];
    const arrived = target ? minorOf(s.targetAmounts[target], s.context.currency) : null;
    if (arrived === null || !s.sendsBank) return arrived;
    const shortfall = shortfallMinor(s);
    return shortfall === null ? null : arrived + shortfall;
  }
  const bankMinor = minorOf(s.bank, s.context.currency);
  const extras = linesMinor(s);
  if (bankMinor === null || extras === null) return null;
  return computeSettlePreview({
    bankMinor,
    extras,
    withheld: [],
    dependents: dependentsMinor.map((amountMinor) => ({ amountMinor })),
  }).settleMinor;
}

/**
 * The single settle's bank amount — what the request sends as `amount_minor`.
 * Counting from the bank it is the bank as typed or suggested; counting from
 * the payment amount (OH-201) it follows the server's equation the other way
 * round: settle + Σincome + Σexpense − Σwithheld + Σdependents. In the fee
 * mode (R3′) the shortfall line is in both the settle and the withheld sum
 * and cancels: the bank is what arrived plus the operator's lines, signed.
 */
export function singleBankMinor(s: DraftState, dependentsMinor: number[]): number | null {
  if (s.anchor === 'bank') return minorOf(s.bank, s.context.currency);
  const settle = singleSettleMinor(s, dependentsMinor);
  const lines = linesMinor(s);
  if (settle === null || lines === null) return null;
  const lineSum = lines.reduce((sum, l) => sum + (l.kind === 'withheld' ? -l.amountMinor : l.amountMinor), 0);
  return settle + lineSum + dependentsMinor.reduce((sum, d) => sum + d, 0);
}

/**
 * The bank amount a single form's fee-mode preview asks about (OH-202): the
 * bank before the operator's own lines. The server suggests the shortfall as
 * «plan − bank» and knows nothing of those lines — asked about the bank
 * itself, it would cover them a second time, and an operator who enters what
 * arrived plus the fee the bank charged separately would be off by that fee.
 * Suggested lines are the server's own (the shortfall itself among them) and
 * stay out. Null when the bank or an operator line does not parse. Counting
 * from the bank this is what arrived (arrivedMinor); counting from the
 * payment amount (R3′) what arrived is the payment field itself.
 */
export function bankBeforeOperatorLinesMinor(s: DraftState): number | null {
  let sum = minorOf(s.bank, s.context.currency);
  if (sum === null) return null;
  for (const l of liveLines(s)) {
    if (l.source !== 'operator') continue;
    const v = minorOf(l.amount, s.context.currency);
    if (v === null) return null;
    sum += l.kind === 'withheld' ? v : -v;
  }
  return sum;
}

// ---- the plan and the fee mode (OH-205 §6) ---------------------------------------

/** planned_minor of a target in the current answer to the draft's own context, in either request shape (plan decision 5). */
export function plannedMinorOf(s: DraftState, key: TargetKey): number | null {
  // The plan does not depend on the bank amount the request carried, so switching the fee mode — which
  // changes the request shape and with it the answer the draft waits for — does not take the plan away.
  const suggestion = s.suggestion;
  if (!suggestion || !sameContext(suggestion.context, s.context)) return null;
  const target = suggestion.response.targets.find((t) => targetKey(t) === key);
  return target ? target.planned_minor : null;
}

/** plannedMinorOf for the single form's one target. */
export function singlePlannedMinor(s: DraftState): number | null {
  const target = s.context.targets[0];
  return target ? plannedMinorOf(s, target) : null;
}

/** Σ of the live shortfall lines — the part of the plan the fee mode asks the bank to have kept; null when one does not parse. */
export function shortfallMinor(s: DraftState): number | null {
  let sum = 0;
  for (const l of liveLines(s)) {
    if (l.source !== 'shortfall') continue;
    const v = minorOf(l.amount, s.context.currency);
    if (v === null) return null;
    sum += v;
  }
  return sum;
}

/**
 * What arrived, whichever request shape the form is in: counting from the payment amount — the payment field, typed or
 * suggested; from the bank — bankBeforeOperatorLinesMinor. Only what the operator entered counts: a suggested line (the
 * shortfall included) and an amount typed on one are no part of it, so switching the fee mode on or off moves nothing
 * here — the number a window can decide by without reading its own mode. Null when it does not parse. From the bank it
 * reads bankBeforeOperatorLinesMinor whatever the hook's `bankBeforeOperatorLines` flag says: both single windows
 * pass `true`.
 */
export function arrivedMinor(s: DraftState): number | null {
  if (s.anchor === 'bank') return bankBeforeOperatorLinesMinor(s);
  const target = s.context.targets[0];
  return target ? minorOf(s.targetAmounts[target], s.context.currency) : null;
}

/**
 * The fee mode's «what arrived» (arrivedMinor): null outside the mode, where the request asks nothing about it. A form
 * passing `bankBeforeOperatorLines: false` asks the server about the bank itself, so with operator lines (which the
 * server knows nothing of) the shortfall it brings does not fit this reading and the fee mode could not close from the
 * bank; without operator lines `previewBankMinor(s, false)` and this are the same number.
 */
export function feeArrivedMinor(s: DraftState): number | null {
  return s.sendsBank ? arrivedMinor(s) : null;
}

/**
 * planned − (arrived + Σ live shortfall lines); 0 ⇔ the fee mode closes the plan; null when any part is
 * unknown. Positive — still short of the plan, negative — over it. The shortfall line the draft holds is the
 * one of the LAST answer: «arrived» edited since is a gap until the next answer brings the new line.
 */
export function feeGapMinor(s: DraftState): number | null {
  const planned = singlePlannedMinor(s);
  const arrived = feeArrivedMinor(s);
  const shortfall = shortfallMinor(s);
  if (planned === null || arrived === null || shortfall === null) return null;
  return planned - (arrived + shortfall);
}

/**
 * The bank_amount_minor the preview request carries; null — the request goes without one. Only in the fee
 * mode, and only for an amount the operator typed in this context (manual, not stale, in its currency, parsed):
 * a suggested value is the server's own number — sending it back would only echo it. The fee mode asks about
 * what arrived: counting from the payment amount that is the payment field; counting from the bank, the bank
 * itself — or, for a form whose operator lines the server knows nothing of (OH-202), the bank before them.
 */
export function previewBankMinor(s: DraftState, bankBeforeOperatorLines: boolean): number | null {
  if (!s.sendsBank) return null;
  if (s.anchor === 'settle') {
    const target = s.context.targets[0];
    const arrived = target ? s.targetAmounts[target] : undefined;
    return arrived?.manual && !arrived.stale ? minorOf(arrived, s.context.currency) : null;
  }
  if (!s.bank.manual || s.bank.stale) return null;
  return bankBeforeOperatorLines ? bankBeforeOperatorLinesMinor(s) : minorOf(s.bank, s.context.currency);
}

/**
 * The answer without its suggested shortfall line — what Apply fills the single form with when it counts from
 * the payment amount in the fee mode (R3′, applySuggestion): the payment is the plan again, and the shortfall
 * belongs to the arrival Apply discards. Nothing else of the answer changes, the input is not touched.
 */
export function withoutShortfall(r: FinanceSettlePreviewResponse): FinanceSettlePreviewResponse {
  return { ...r, suggested_lines: r.suggested_lines.filter((l) => l.source !== 'shortfall') };
}

/** The accepted base's movement rates by currency (EUR = 1 000 000); null without a base. */
export function baseRates(s: DraftState): Record<string, number> | null {
  if (!s.base) return null;
  // money.RateMicroEUR: EUR is the unit every rate is quoted against, listed in the answer or not
  return { ...Object.fromEntries(s.base.expect.rates.map((r) => [r.currency, r.rate_micro])), EUR: 1_000_000 };
}

/** A target's remaining plan and currency in the accepted base; null when the base does not hold it. */
export function baseTargetOf(s: DraftState, key: TargetKey): { remainingMinor: number; currency: string } | null {
  const target = s.base?.expect.targets.find((t) => targetKey(t) === key);
  return target ? { remainingMinor: target.remaining_minor, currency: target.currency } : null;
}

/**
 * Everything a window compares for one target, in the server's arithmetic (plan decision 9, [PL-02]):
 * the payment (account currency) against plannedMinorOf, atPlan by the base's rates, the remaining plan and
 * the payment converted into the target's currency. One currency: planned = remaining, settled = paid.
 * Null until the base and the plan are known or when a conversion has no rate. Meaningful in the status 'clean':
 * in a conflict or a stale draft `plannedMinor` comes from the newer answer and `remainingMinor` from the older
 * base, so a window compares a clean draft only. Not only a `draftReady` one: a clean draft whose held answer is
 * for the other request shape (the fee mode was just switched) reads the same plan — `plannedMinorOf` ignores
 * `sentBank` — and waiting for the new shape's answer would only blink the comparison out.
 */
export function targetComparison(
  s: DraftState,
  key: TargetKey,
  paidMinor: number,
): (PlanComparison & { remainingMinor: number; settledMinor: number }) | null {
  const target = baseTargetOf(s, key);
  const rates = baseRates(s);
  const plannedMinor = plannedMinorOf(s, key);
  if (!target || !rates || plannedMinor === null) return null;
  const accountCurrency = s.context.currency;
  const settledMinor = convertMinor(paidMinor, accountCurrency, target.currency, rates);
  if (settledMinor === null) return null;
  return {
    paidMinor,
    plannedMinor,
    crossCurrency: accountCurrency !== target.currency, // by code, never by rate
    atPlan: atPlan({
      paidMinor,
      plannedMinor,
      accountCurrency,
      remainingMinor: target.remainingMinor,
      targetCurrency: target.currency,
      rates,
    }),
    remainingMinor: target.remainingMinor,
    settledMinor,
  };
}

/**
 * The single form's admission [PR-03]: no zero-difference rule in general —
 * a partial payment, a remainder mode, dependents and settled_minor work as
 * before. Zero difference only where the amount is fixed: a PSP-fact
 * projection (bank + lines = the fact), and the «bank kept the difference as a
 * fee» mode, which closes at feeGapMinor = 0 — what arrived plus the shortfall
 * line is the plan; an unknown gap is no zero — and also excludes dependents
 * and an explicit settled_minor [PR3-02].
 */
export function canSubmitSingle(
  s: DraftState,
  o: { isProjection: boolean; feeMode: boolean; dependentsMinor: number[]; settledOverride: boolean },
): boolean {
  if (!draftReady(s)) return false;
  const settle = singleSettleMinor(s, o.dependentsMinor);
  if (settle === null || settle <= 0) return false;
  if (s.anchor === 'settle') {
    // OH-201: the derived bank amount must be one the request can carry
    const bankMinor = singleBankMinor(s, o.dependentsMinor);
    if (bankMinor === null || bankMinor <= 0) return false;
  }
  if (o.isProjection && draftDifferenceMinor(s) !== 0) return false;
  if (o.feeMode && (feeGapMinor(s) !== 0 || o.dependentsMinor.length > 0 || o.settledOverride)) return false;
  return true;
}

/**
 * The live lines as request lines. A line bound to a target (`targetKey`, any
 * source) carries the booking and apartment of THAT target in the current
 * suggestion (§4.3: «с бронью и квартирой цели») — the draft keeps no
 * attribution of its own, and a fee the operator kept after the suggestion
 * dropped it is still the fee of that booking. Lines without a target (the
 * shortfall, operator lines with none) are sent unattributed, as the server
 * does. The target is always in the response: `draftReady` requires a current
 * suggestion and live lines are never orphans.
 */
export function draftLineInputs(s: DraftState): FinanceLineInput[] {
  const resp = s.suggestion?.response;
  return liveLines(s).map((l) => {
    const minor = minorOf(l.amount, s.context.currency) ?? 0;
    const input: FinanceLineInput = { kind: l.kind, category_id: l.categoryId, amount_minor: minor };
    const target = l.targetKey !== null ? resp?.targets.find((t) => targetKey(t) === l.targetKey) : undefined;
    if (target?.booking_id) input.booking_id = target.booking_id;
    if (target?.apartment_id) input.apartment_id = target.apartment_id;
    return input;
  });
}
